/**
 * "Move in a resident" guided flow — the NEW resident path.
 *
 * Scope note (Decision 1): this teaches admitting a new resident via
 * "New Resident" → ResidentDrawer. Bringing a former resident back is the
 * separate "Move Back In" button on an inactive resident's profile and is
 * deliberately out of scope for v1 — it is a different workflow, not a branch of
 * this one.
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §6.3.
 */

export default {
    id: 'move-in-resident',
    title: 'Move in a resident',
    description: 'Add a new resident to the community.',

    minRoleWeight: 11,

    /* Exactly complementary to moveInResidentEhr: when the EHR owns resident
       data, that flow teaches the task-approval procedure instead and this one
       disappears for everyone. No weight-80 override — on such a campus the
       correct process IS approving the EHR's task, so guiding a super-admin
       through the portal drawer would teach the wrong thing. The Add button
       itself stays available to them (ResidentsPage `canEditResidents` keeps its
       own override); this is about which procedure the guide teaches. */
    available: (ctx) => !ctx.ehrActive,

    startsAt: 'This will take you to the Residents page.',

    steps: [
        {
            id: 'open-drawer',
            route: { path: '/community/residents', navigate: true },
            anchor: 'residents.new-resident',
            title: 'Create a new resident',
            body: 'Click +New Resident to open the add form.',
            placement: 'bottom',
            /* The drawer's submit button existing means the drawer is open. */
            done: { type: 'dom', selector: '[data-help-id="resident-drawer.submit"]' },
        },
        {
            id: 'fill-fields',
            route: { path: '/community/residents', navigate: false },
            anchor: 'resident-drawer.area',
            title: 'Choose the area, then add their name',
            body: 'Pick or search room they are moving into, then type in first and last name. Rooms must be available to move them in.',
            placement: 'left',
            /* The drawer already computes exactly this: its submit button is
               `disabled={!isFormValid || saving}`, where isFormValid is
               firstName && lastName && areaId && (!offsiteActive || phone)
               (ResidentDrawer.jsx:189). So the button becoming enabled IS
               "required fields are filled in" — no need to watch keystrokes, and
               no need to ask the user to confirm something the form already knows.
               The :not([disabled]) selector is what turns validation into the
               completion signal. */
            done: {
                type: 'dom',
                selector: '[data-help-id="resident-drawer.submit"]:not([disabled])',
            },
            context: {
                selector: '[data-help-id="resident-drawer.submit"]',
                contextStep: 'open-drawer',
            },
        },
        {
            id: 'submit',
            route: { path: '/community/residents', navigate: false },
            anchor: 'resident-drawer.submit',
            title: 'Add the resident',
            body: 'Click Add Resident. The button stays disabled until the required fields are filled in.',
            placement: 'top',
            context: {
                selector: '[data-help-id="resident-drawer.submit"]',
                contextStep: 'open-drawer',
            },
            /* resident_move_in_profile: the duplicate dialog can move a former resident
               back in instead of adding them, which also completes the step. */
            done: { type: 'transaction', transTypes: ['resident_add', 'resident_move_in_profile'] },
            timeoutMs: 15000,
        },
    ],

    success: {
        title: 'Resident added',
        body: 'They are now active in their area. Next you might want to assign them a pendant, or set up their wellness check-ins.',
    },
};
